<template>
	<div class="palta-checkout">
		<div class="palta-checkout__item" v-show="canShowPaymentMethod('card')">
			<CardForm
				:cta="mergedCardProps.cta"
				:button-class-list="buttonClassList"
				:rendered="isRendered"
				:teleport-to="mergedCardProps.teleportTo"
				@submit="onSubmitCardForm"
			>
				<template #beforeButton>
					<slot name="beforeButton"></slot>
				</template>
			</CardForm>
		</div>
		<div class="palta-checkout__item" v-show="canShowPaymentMethod('paypal')">
			<PayPalButton
				:cta="mergedPaypalProps.cta"
				:class-list="buttonClassList"
				:teleport-to="mergedPaypalProps.teleportTo"
			/>
		</div>
		<div class="palta-checkout__item" v-show="canShowPaymentMethod('apple')">
			<ApplePayButton :teleport-to="mergedApplePayProps.teleportTo" @ready="onPaymentMethodReady('apple')" />
		</div>
	</div>
</template>

<script>
import { createPaymentClient } from '@palta-brain/payments'
import uuid from 'uuid-random'
import { mapActions } from 'vuex'

import config from '@/config'
import { PAYMENT_METHODS } from '../helpers/constants'
import { getDefaultPaypalOptions, getDefaultApplePayOptions, getDefaultCardOptions } from '../helpers/checkoutOptions'
import { mergeDeep } from '@/helpers/utils'

import CardForm from './CardForm.vue'
import PayPalButton from './PayPalButton.vue'
import ApplePayButton from './ApplePayButton.vue'

export default {
	name: 'PaltaCheckout',

	components: { CardForm, PayPalButton, ApplePayButton },

	props: {
		ident: {
			type: String,
			required: true
		},

		displayedPaymentMethods: {
			type: Array,
			default: () => ['card', 'paypal'],
			validator: (value) => {
				return Object.keys(PAYMENT_METHODS).map((key) => value.includes(key))
			}
		},

		availablePaymentMethods: {
			type: Array,
			default: () => ['card', 'paypal', 'apple']
		},

		email: {
			type: String,
			required: true
		},

		locale: {
			type: String,
			default: 'en'
		},

		geoCountryCode: {
			type: String,
			default: 'US'
		},

		cardOptions: {
			type: Object,
			default: () => ({})
		},

		paypalOptions: {
			type: Object,
			default: () => ({})
		},

		applePayOptions: {
			type: Object,
			default: () => ({})
		},
		theme: {
			type: String,
			default: 'light',
			validator: (value) => {
				return ['light', 'dark'].includes(value)
			}
		}
	},

	data() {
		return {
			isRendered: false,
			buttonStates: {
				isVisible: false,
				isDisabled: false,
				isLoading: false
			},
			client: null,
			checkoutForm: null,
			pendingCheckout: null,
			customerId: null,
			lastUsedPaymentMethod: null
		}
	},

	computed: {
		mergedCardProps() {
			const defaultProps = {
				cta: this.$t('common.controls.continue'),
				options: {
					...getDefaultCardOptions({
						theme: this.theme
					})
				}
			}
			return mergeDeep(defaultProps, this.cardOptions)
		},

		mergedPaypalProps() {
			const defaultProps = {
				cta: this.$t('common.controls.purchase_button'),
				options: {
					...getDefaultPaypalOptions({
						theme: this.theme
					}),
					onClick: () => {
						this.emitSubmit('paypal')
					}
				}
			}
			return mergeDeep(defaultProps, this.paypalOptions)
		},

		mergedApplePayProps() {
			const defaultProps = {
				options: {
					...getDefaultApplePayOptions({
						theme: this.theme
					})
				}
			}
			return mergeDeep(defaultProps, this.applePayOptions)
		},

		buttonClassList() {
			return {
				disabled: this.buttonStates.isDisabled || !this.isRendered,
				loading: this.buttonStates.isLoading || !this.isRendered
			}
		},

		paymentMethods() {
			return Object.entries(PAYMENT_METHODS).reduce((filteredMethods, [key, value]) => {
				if (this.availablePaymentMethods.includes(key)) {
					filteredMethods[key] = value
				}
				return filteredMethods
			}, {})
		},

		paymentClientSettings() {
			return {
				ident: this.ident,
				countryCode: this.geoCountryCode.toUpperCase(),
				platformCode: 'desktop_web',
				orderId: uuid(),
				customerId: {
					type: 'merchant-uuid',
					value: this.customerId
				},
				customer: {
					emailAddress: this.email
				}
			}
		},

		/**
		 * For more information and available options, refer to the Universal Checkout documentation at:
		 * https://example.com/universal-checkout/documentation
		 */
		universalCheckoutOptions() {
			return {
				container: `#checkout-form`,
				locale: this.locale,
				...this.mergedCardProps.options,
				paypal: this.mergedPaypalProps.options,
				applePay: this.mergedApplePayProps.options,

				submitButton: {
					useBuiltInButton: false,
					onVisible: (isVisible) => {
						this.buttonStates.isVisible = isVisible
					},

					onDisable: (isDisabled) => {
						this.buttonStates.isDisabled = isDisabled
					},

					onLoading: (isLoading) => {
						this.buttonStates.isLoading = isLoading
					}
				}
			}
		}
	},

	watch: {
		ident() {
			this.updateCheckout()
		}
	},

	methods: {
		...mapActions({
			checkExistingUserByEmail: 'checkExistingUserByEmail'
		}),

		canShowPaymentMethod(paymentMethod) {
			return (
				this.displayedPaymentMethods.includes(paymentMethod) && this.availablePaymentMethods.includes(paymentMethod)
			)
		},

		createClient() {
			const settings = {
				apiEndpoint: config('PaltaApiUrl'),
				apiKey: config('PaltaApiKey'),
				metadata: {
					...this.paymentMethods
				},
				onError: (error, description) => {
					this.$emit('error', {
						error,
						description
					})
				},
				onPaymentStatusChange: this.onPaymentStatusChange
			}

			this.client = createPaymentClient(settings)
		},

		onPaymentStatusChange(status, paymentMethod) {
			if (paymentMethod) {
				this.lastUsedPaymentMethod = paymentMethod
			}
			switch (status) {
				case 'COMPLETE':
					this.emitStatusComplete()
					break
				case 'PENDING':
					this.$emit('pending')
					break
				default:
					break
			}
		},

		emitStatusComplete() {
			this.$emit('loading', true)

			this.$store
				.dispatch('loadPaltaSubscription', {
					ident: this.ident
				})
				.then((subscription) => {
					// FIXME: Temporary event for debug
					this.$analytic.logEvent('Api - Palta', {
						...subscription
					})
					this.$emit('complete', {
						...subscription,
						payment_method: this.lastUsedPaymentMethod
					})
				})
				.catch((e) => {
					this.$emit('error', e)
				})
		},

		emitSubmit(provider) {
			this.$emit('submit', {
				provider
			})
		},

		onPaymentMethodReady(paymentMethod) {
			this.$emit('change', {
				[paymentMethod]: true
			})
		},

		renderCheckout() {
			this.client
				.showPaymentForm(this.paymentClientSettings, this.universalCheckoutOptions)
				.then((checkoutForm) => {
					this.checkoutForm = checkoutForm
					this.$emit('ready')
				})
				.catch(this.handleError)
				.finally(this.handleFinally)
		},

		updateCheckout() {
			this.isRendered = false
			this.client
				.updatePaymentForm(this.paymentClientSettings)
				.then(() => {
					this.$emit('update')
				})
				.catch(this.handleError)
				.finally(this.handleFinally)
		},

		handleError(error) {
			this.$emit('error', error)
		},

		handleFinally() {
			this.isRendered = true
		},

		onSubmitCardForm() {
			this.checkoutForm.submit()
			this.emitSubmit('card')
		}
	},

	mounted() {
		this.$nextTick(() => {
			this.createClient()
			this.checkExistingUserByEmail({ email: this.email }).then((customerId) => {
				this.customerId = customerId
				this.renderCheckout()
			})
		})
	}
}
</script>

<style lang="scss" scoped>
.palta-checkout {
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 30px;
}
</style>
